import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Box, Button, LinearProgress, Modal, Typography } from '@mui/material';
import Tile from '../components/Tile/Tile';
import CompanySearchModal from './CompanySearchModal';
import CircularProgress from '@mui/material/CircularProgress';
import makeStyles from '@mui/styles/makeStyles';
import { Replay } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import IconButton from '@mui/material/IconButton';
import _ from 'lodash';
import gql from 'graphql-tag';
import styles from 'assets/jss/material-dashboard-pro-react/components/tasksStyle.js';
import CloseIcon from '@mui/icons-material/Close';
import { theme } from '../theme';

const COMPANY_SEARCH = gql`
  query ($input: CompanySearchInput!) {
    companySearch(input: $input) {
      id
      companyName
      street
      city
      postalCode
      provinceAbbreviation
      country
      confidenceScore
    }
  }
`;

const useStyles = makeStyles(theme => ({
  ...styles,
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 500,
    maxHeight: '80%',
    '@media (max-width: 768px)': {
      width: '96vw',
      minWidth: '96vw',
    },
  },
  closeButton: {
    margin: 'auto 4px',
    color: theme.palette.grey[500],
    zIndex: '19',
    cursor: 'pointer',
    width: '30px',
    height: '30px',
  },
  pullCreditContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: 500,
    padding: '16px 32px 32px',

    '& button': {
      margin: '32px auto 16px',
    },
  },
  wrapperLoader: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.grey[500],
    width: '100%',
    alignItems: 'center',

    '& .headerContainer': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 32,

      '& h4': {
        marginLeft: 8,
      },
    },
    '& .progressLoader': {
      margin: 32,
      color: theme.palette.grey[500],
    },
  },
}));

const CreditSafeComp = props => {
  const {
    vo,
    creditApp,
    userDocs,
    setCreditPullErrorSnackBarText,
    setOpenCreditPullErrorSnackBar,
    userDocsRefetch,
    refetchCreditApp,
    doneRefetchingCreditApp,
    setDoneRefetchingDocuments,
    setDocumentIdToAutomaticallyOpen,
    vpBusinessCreditReportPullInDealerPortal,
    commercialCreditReportPullButtonLabel,
    isIndividualCreditApp,
  } = props;
  const classes = useStyles();

  // TODO: Add a real loading condition. This just assumes that if the pull in dealer portal setting is DUN_AND_BRADSTREET, then we are loading a report.
  const [businessCreditReportLoading, setBusinessCreditReportLoading] = useState(
    creditApp.isCreditReportBusinessPulling
  );

  const [openCompanySearchModal, setOpenCompanySearchModal] = useState(false);
  const [companySearchResult, setCompanySearchResult] = useState(null);
  const [creditReportDownloaded, setCreditReportDownloaded] = useState(false);
  const [creditReportId, setCreditReportId] = useState(null);
  const [handlingClose, setHandlingClose] = useState(false);

  const [companySearch, { data: searchResult, refetch }] = useLazyQuery(COMPANY_SEARCH, {
    context: { authRequired: true },
    fetchPolicy: 'no-cache',
  });

  const findUserDoc = id => {
    return userDocs?.documents?.find(d => d.documentId === id);
  };

  const handleViewCreditReportBusiness = id => {
    const doc = findUserDoc(id);
    if (doc) window.open(doc.source, '_newtab');
    return;
  };

  async function triggerCommercialCreditPull() {
    setBusinessCreditReportLoading(true);
    let variables;
    if (isIndividualCreditApp) {
      variables = {
        input: {
          countryAbbreviation: 'US',
          companyName: `${creditApp.firstName} ${creditApp.lastName}`,
          address: creditApp.address,
          city: creditApp.city,
          postalCode: creditApp.postalCode,
          provinceAbbreviation: creditApp.state,
          dba: creditApp.dba,
          phone: creditApp.phone,
          taxId: creditApp.ssn ? creditApp.ssn.toString() : '',
          // dynamicsCreditApplicationId: creditApp.dynamicsId,
          creditReporterCode: vpBusinessCreditReportPullInDealerPortal, // PAYNET, CREDITSAFE or DUN_AND_BRADSTREET
        },
      };
    } else {
      variables = {
        input: {
          countryAbbreviation: 'US',
          companyName: creditApp.businessName,
          address: creditApp.address,
          city: creditApp.city,
          postalCode: creditApp.postalCode,
          provinceAbbreviation: creditApp.state,
          dba: creditApp.dba,
          phone: creditApp.phone,
          taxId: creditApp.taxId ? creditApp.taxId.toString() : '',
          // dynamicsCreditApplicationId: creditApp.dynamicsId,
          creditReporterCode: vpBusinessCreditReportPullInDealerPortal, // PAYNET, CREDITSAFE or DUN_AND_BRADSTREET
        },
      };
    }
    await companySearch({
      variables,
    });
    // await triggerCreditReportBusinessPull({
    //   variables,
    // });
    // setTimeout(async () => {
    //   await refetchCreditApp();
    //   setDoneRefetchingCreditApp(true);
    // }, 1000 * 7);
  }

  const handleViewDownloadedCreditReportDocument = creditReportDocId => {
    setDocumentIdToAutomaticallyOpen(creditReportDocId);
  };

  const handleCompanySearchModalClose = async creditApp => {
    setHandlingClose(true);
    if (creditReportDownloaded) {
      await userDocsRefetch();
      await refetchCreditApp();
    }
    setOpenCompanySearchModal(false);
    setHandlingClose(false);
    // console.log(`CREDIT REPORT DOC ID: ${creditReportDocId}`);
    // setDocumentIdToAutomaticallyOpen(creditReportDocId);
    setDoneRefetchingDocuments(true);
    // if (creditApp?.mostRecentCreditReportBusiness?.humanReadableReportDocumentId) {
    //   handleViewCreditReportBusiness(creditApp.mostRecentCreditReportBusiness.humanReadableReportDocumentId);
    // }
    setCompanySearchResult(null);
    setCreditReportDownloaded(false);
    setCreditReportId(null);
  };

  useEffect(() => {
    if (businessCreditReportLoading && doneRefetchingCreditApp) {
      const businessReport = creditApp.mostRecentCreditReportBusiness;
      setBusinessCreditReportLoading(false);
      if (businessReport && businessReport.humanReadableReportDocumentId) {
        setOpenCreditPullErrorSnackBar(false);
      } else {
        setCreditPullErrorSnackBarText('Could not pull commercial credit report. Please contact support.');
        setOpenCreditPullErrorSnackBar(true);
      }
    }
  }, [businessCreditReportLoading, doneRefetchingCreditApp]);

  useEffect(() => {
    if (searchResult && businessCreditReportLoading) {
      console.log('--------------------------------------');
      console.log(JSON.stringify(searchResult));
      console.log('--------------------------------------');
      setBusinessCreditReportLoading(false);
      setCompanySearchResult(searchResult);
    }
  }, [searchResult, businessCreditReportLoading]);

  const docId = _.get(creditApp, 'mostRecentCreditReportBusiness.humanReadableReportDocumentId', null);

  useEffect(() => {
    setBusinessCreditReportLoading(false);
  }, [docId]);

  useEffect(() => {
    if (
      vpBusinessCreditReportPullInDealerPortal !== 'DUN_AND_BRADSTREET' ||
      vpBusinessCreditReportPullInDealerPortal !== 'EQUIFAX_COMMERCIAL' ||
      docId
    )
      return;
    if (creditApp?.createdDateTime && new Date(creditApp.createdDateTime) < new Date('2026-05-12T09:45:00-05:00'))
      return;
    const timeout = setTimeout(async () => {
      await refetchCreditApp();
      await userDocsRefetch();
      setBusinessCreditReportLoading(false);
      setDoneRefetchingDocuments(true);
    }, 15000);
    return () => clearTimeout(timeout);
  }, [vpBusinessCreditReportPullInDealerPortal, docId]);

  const docExists = findUserDoc(docId) ? true : false;

  return (
    <span>
      {businessCreditReportLoading && (
        <Box sx={{ width: 200 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Fetching credit report...
          </Typography>
        </Box>
      )}
      {_.get(creditApp, 'mostRecentCreditReportBusiness.humanReadableReportDocumentId', null) && docExists && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              color: theme.palette.primary.main,
              cursor: 'pointer',
              textDecoration: 'underline',
              marginRight: 4,
            }}
            onClick={() =>
              handleViewCreditReportBusiness(creditApp.mostRecentCreditReportBusiness.humanReadableReportDocumentId)
            }
          >
            View Credit Report
          </div>
          {!businessCreditReportLoading && (
            <>
              <IconButton
                onClick={() => setOpenCompanySearchModal(true)}
                title="Search Credit Report Again"
                style={{ padding: 4 }}
                size="large"
              >
                <Replay />
              </IconButton>
            </>
          )}
        </div>
      )}
      {(!creditApp.mostRecentCreditReportBusiness || !docExists) && (
        <span>
          {!businessCreditReportLoading && (
            <Button
              size="small"
              color="primary"
              variant="contained"
              style={{ marginLeft: 0 }}
              onClick={() => setOpenCompanySearchModal(true)}
            >
              {commercialCreditReportPullButtonLabel}
            </Button>
          )}
        </span>
      )}
      <Modal open={openCompanySearchModal} onClose={handleCompanySearchModalClose} aria-labelledby="Company Search">
        <div className={classes.modalStyle}>
          <Tile
            title="Credit Report Search"
            titleButtonComponent={
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={() => {
                  handleCompanySearchModalClose();
                }}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            }
          >
            {handlingClose && (
              <div className={classes.wrapperLoader}>
                <CircularProgress className="progressLoader" size={48} />
                <div className="headerContainer">
                  <h4>Please wait...</h4>
                </div>
              </div>
            )}
            {businessCreditReportLoading && (
              <div className={classes.wrapperLoader}>
                <CircularProgress className="progressLoader" size={48} />
                <div className="headerContainer">
                  <SearchIcon />
                  <h4>Searching for Company...</h4>
                </div>
              </div>
            )}
            {!handlingClose && !!companySearchResult && !creditReportDownloaded ? (
              <CompanySearchModal
                vo={vo}
                companySearchResult={companySearchResult}
                creditApp={creditApp}
                refetchCreditApp={refetchCreditApp}
                userDocsRefetch={userDocsRefetch}
                setCreditReportDownloaded={setCreditReportDownloaded}
                setCreditReportId={setCreditReportId}
                vpBusinessCreditReportPullInDealerPortal={vpBusinessCreditReportPullInDealerPortal}
              />
            ) : (
              <>
                {!handlingClose && !businessCreditReportLoading && !creditReportDownloaded && (
                  <div className={classes.pullCreditContainer}>
                    <p>Search credit report for applicant:</p>
                    <br />
                    <div style={{ margin: '0 auto' }}>
                      <p>{creditApp.businessName}</p>
                      <p>{creditApp.address}</p>
                      <p>
                        {creditApp.city}, {creditApp.state} {creditApp.postalCode}
                      </p>
                    </div>
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={() => triggerCommercialCreditPull(creditApp.id)}
                    >
                      Search
                    </Button>
                  </div>
                )}
              </>
            )}
            {!handlingClose && creditReportDownloaded && (
              <div className={classes.pullCreditContainer}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CheckIcon style={{ color: '#4CAF50' }} />
                  <h4 style={{ marginLeft: 4 }}>Credit Report Downloaded</h4>
                </div>
                <br />
                <p style={{ textAlign: 'center' }}>The report has been added to the opportunity documents section.</p>
                <Button
                  size="small"
                  color="primary"
                  variant="contained"
                  onClick={() => handleViewCreditReportBusiness(creditReportId)}
                >
                  View Report
                </Button>
              </div>
            )}
          </Tile>
        </div>
      </Modal>
    </span>
  );
};

export default CreditSafeComp;
